/**
 * NotificationBell (P133.3, #480) -- header bell + unread badge +
 * dropdown feed. Clicking a row marks it read and navigates to its
 * deep link. Polls the unread count every 60s (no live push at MVP).
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  useMarkAllRead,
  useAcknowledge,
  useMarkRead,
  useNotifications,
  useUnreadCount,
} from "shared/notifications/notification_hooks";
import { useSetDigestMode, useUserPreferences } from "shared/api/userPreferences";
import type { AppNotification } from "shared/api/types";
import { alignedLeft } from "shared/notifications/clampToViewport";

const SEVERITY_TONE: Record<string, string> = {
  critical: "bg-rose-100 text-rose-900",
  high: "bg-amber-100 text-amber-900",
};

/** The severity a notification is ABOUT, when it carries one. */
function severityOf(n: AppNotification): string | null {
  const raw = n.payload?.["severity"];
  return typeof raw === "string" && raw in SEVERITY_TONE ? raw : null;
}

export function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const unread = useUnreadCount();
  const count = unread.data?.count ?? 0;
  const feed = useNotifications();
  const markRead = useMarkRead();
  const acknowledge = useAcknowledge();
  const markAll = useMarkAllRead();
  const prefs = useUserPreferences();
  const setDigest = useSetDigestMode();
  const digestMode = prefs.data?.digest_mode ?? null;
  /* Round 19 G34 -- opening the bell is reading it: the treasurer's two
     Route 2 rows stayed unread after he had viewed them. The rows new on
     this opening keep their marker while the panel is open, so marking
     them read does not hide which ones they were. */
  const [newOnOpen, setNewOnOpen] = useState<Set<string>>(new Set());

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // D5 -- the panel was drawn half off the left edge of the window, so
  // half the notifications could not be read. Anchoring is a layout
  // rule; staying inside the window is a measurement.
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [offset, setOffset] = useState(0);
  useLayoutEffect(() => {
    if (!open) {
      setOffset(0);
      return;
    }
    const el = panelRef.current;
    const trigger = ref.current;
    if (!el || !trigger) return;
    const rect = el.getBoundingClientRect();
    const t = trigger.getBoundingClientRect();
    // C4 -- align to the bell, then clamp. The reverse order is how the
    // panel came to be both inside the window and 165px from the bell
    // that opened it, for four rounds running.
    setOffset(
      alignedLeft({
        triggerLeft: t.left,
        triggerRight: t.right,
        panelWidth: rect.width,
        viewportWidth: window.innerWidth,
      }) - rect.left,
    );
  }, [open]);

  function openRow(n: AppNotification) {
    if (n.status === "unread") markRead.mutate(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid="notification-bell"
        aria-label={t("notifications.bellLabel")}
        onClick={() => {
          if (!open) {
            // Round 20 U9 -- the list was fetched once and kept; only the
            // unread count was polled. Opening the panel asks again.
            void feed.refetch();
            const fresh = (feed.data ?? []).filter((n) => n.status === "unread");
            setNewOnOpen(new Set(fresh.map((n) => n.id)));
            if (fresh.length > 0) markAll.mutate();
          }
          setOpen((v) => !v);
        }}
        className="relative rounded px-2 py-1 text-sky-nav hover:text-brand-cream"
      >
        {/* ROUND 15 G5 -- an emoji is a picture the platform chooses;
            this is a glyph the product draws, in the current colour. */}
        <svg
          aria-hidden
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="inline-block align-[-0.15em]"
        >
          <path d="M6 17V11a6 6 0 0 1 12 0v6" />
          <path d="M4 17h16" />
          <path d="M10 20a2 2 0 0 0 4 0" />
        </svg>
        {count > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -end-0.5 -top-0.5 min-w-[15px] h-[15px] rounded-full bg-brand-brass px-1 text-center font-mono text-[9px] font-bold text-[#1d1607]"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          data-testid="notification-dropdown"
          style={offset ? { transform: `translateX(${offset}px)` } : undefined}
          className="absolute end-0 z-50 mt-2 w-80 rounded-lg border border-slate-200 bg-white text-slate-800 shadow-2xl"
        >
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
            <span className="text-sm font-medium">{t("notifications.heading")}</span>
            {count > 0 && (
              <button
                type="button"
                data-testid="notification-mark-all"
                className="text-xs text-brand-brassDark hover:underline"
                onClick={() => markAll.mutate()}
              >
                {t("notifications.markAll")}
              </button>
            )}
          </div>
          <ul className="max-h-80 overflow-y-auto">
            {(feed.data ?? []).length === 0 ? (
              <li
                data-testid="notification-empty"
                className="px-3 py-6 text-center text-xs text-slate-400"
              >
                {t("notifications.empty")}
              </li>
            ) : (
              (feed.data ?? []).map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    data-testid={`notification-item-${n.id}`}
                    onClick={() => openRow(n)}
                    data-new={
                      n.status === "unread" || newOnOpen.has(n.id) ? "true" : "false"
                    }
                    className={`block w-full px-3 py-2 text-start hover:bg-slate-50 ${
                      n.status === "unread" || newOnOpen.has(n.id)
                        ? "bg-brand-brass/5"
                        : ""
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {(n.status === "unread" || newOnOpen.has(n.id)) && (
                        <span
                          aria-hidden
                          className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-brass"
                        />
                      )}
                      {/* B5: severity on the bell. A critical incident
                          arriving in the same grey as a routine
                          hand-off is a critical incident nobody opens
                          first. */}
                      {severityOf(n) && (
                        <span
                          data-testid={`notification-severity-${n.id}`}
                          className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                            SEVERITY_TONE[severityOf(n) ?? ""] ??
                            "bg-slate-100 text-slate-700"
                          }`}
                        >
                          {severityOf(n)}
                        </span>
                      )}
                      <span className="text-sm font-medium text-slate-800">
                        {n.message_key
                          ? t(n.message_key, {
                              ...(n.message_params ?? {}),
                              defaultValue: n.title,
                            })
                          : n.title}
                      </span>
                    </span>
                    {n.body && (
                      <span className="mt-0.5 block text-xs text-slate-500">
                        {n.body}
                      </span>
                    )}
                  </button>
                  {n.ack_required && !n.acknowledged_at && (
                    <div className="px-3 pb-2">
                      <button
                        type="button"
                        data-testid={`notification-ack-${n.id}`}
                        onClick={() => acknowledge.mutate(n.id)}
                        className="rounded bg-brand-brass px-2 py-0.5 text-xs font-medium text-brand-navy hover:bg-brand-brassDark"
                      >
                        {t("notifications.acknowledge")}
                      </button>
                    </div>
                  )}
                </li>
              ))
            )}
          </ul>
          <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-3 py-2">
            <label htmlFor="digest-mode" className="text-xs text-slate-500">
              {t("notifications.digestLabel")}
            </label>
            <select
              id="digest-mode"
              data-testid="notification-digest-select"
              className="rounded border border-slate-300 px-1.5 py-1 text-xs"
              value={digestMode ?? "off"}
              disabled={setDigest.isPending}
              onChange={(e) =>
                setDigest.mutate(e.target.value as "off" | "daily" | "weekly")
              }
            >
              <option value="off">{t("notifications.digestOff")}</option>
              <option value="daily">{t("notifications.digestDaily")}</option>
              <option value="weekly">{t("notifications.digestWeekly")}</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
